/**
 * Per-cell seat cap — mirrored from the backend's
 * `backend/server/src/common/seat-capacity.ts`, field for field, so the
 * designer blocks the exact same writes the API would refuse and reports the
 * exact same numbers. Keep this file's logic identical to that one; if the
 * server's rule ever changes, this is the other half that has to change with
 * it.
 *
 * A room is rendered in the 3D twin as a 4x4 m cell (or several, merged). A
 * walking figure needs floor space, so a cell that is wall-to-wall desks and
 * seated people leaves nothing to walk through. The rule: a room may hold at
 * most 2 seats per grid cell it occupies. Seats = the sum of `table.capacity`
 * over the room's REAL tables; cells = `row_span * col_span`.
 *
 * It was 4 until a desk_4 was measured against a cell: 3.5 x 1.5 m inside
 * 4 x 4 m leaves 0.25 m each side, narrower than a person. A four-person room
 * therefore needs two cells.
 *
 * ── Why this is a TRANSITION rule, not a state rule ──────────────────────
 * 28 of 99 occupied rooms already exceed this cap (live data, pre-dating the
 * rule). A plain "reject if seats > limit" check would make every one of
 * those rooms uneditable — including un-editable to FIX, because fixing a
 * room (removing a table, relabelling it, merging it into more cells)
 * requires saving it, and a naive state check refuses that save too. So a
 * write is allowed if EITHER:
 *   (a) the resulting state is within the cap, OR
 *   (b) the resulting state is no worse than what was already there — seats
 *       did not go UP and cells did not go DOWN.
 * That lets an over-capacity room keep being edited without ever being forced
 * to comply in one step, while a room that is already compliant can never be
 * pushed over: for a compliant room, any increase in seats (cells unchanged)
 * fails both (a) and (b) the moment it crosses the limit.
 *
 * DO NOT "simplify" this to `seats <= limit` — that is exactly the change
 * that would make the already-over-cap rooms unsalvageable.
 */

export const MAX_SEATS_PER_CELL = 2;

/** The seat ceiling for a room occupying this many grid cells. */
export function maxSeatsForCells(cells) {
  return Math.max(1, cells) * MAX_SEATS_PER_CELL;
}

/**
 * Decide whether a write that changes a room's seat count and/or cell count
 * may proceed. All four inputs are plain numbers: the room's state BEFORE the
 * write (`currentSeats`, `currentCells`) and AFTER it would apply
 * (`nextSeats`, `nextCells`). Pure — no network, no React — so it can run
 * client-side before the round-trip that would otherwise be the only way to
 * find out.
 */
export function checkSeatCapacityTransition(currentSeats, currentCells, nextSeats, nextCells) {
  const limit = maxSeatsForCells(nextCells);
  const cellsForMessage = Math.max(1, nextCells);

  if (nextSeats <= limit) {
    return {
      allowed: true,
      code: "within-cap",
      message: `${nextSeats} seat(s) across ${cellsForMessage} cell(s) is within the ${limit}-seat cap.`,
    };
  }

  // Over the cap. Allow it anyway if it does not make the room any worse than
  // it already was: seats did not increase and cells did not decrease.
  const notWorse = nextSeats <= currentSeats && nextCells >= currentCells;
  if (notWorse) {
    return {
      allowed: true,
      code: "legacy-no-regression",
      message:
        `This room already holds ${currentSeats} seat(s) over its ${maxSeatsForCells(currentCells)}-seat cap; ` +
        `the write does not add seats or remove cells (now ${nextSeats} seat(s) / ${cellsForMessage} cell(s)), so it is allowed.`,
    };
  }

  return {
    allowed: false,
    code: "seat-capacity-exceeded",
    message: `This room has ${cellsForMessage} cell(s) and would hold ${nextSeats} seats; the limit is ${limit} (${MAX_SEATS_PER_CELL} per cell).`,
  };
}
